/* eslint-disable @typescript-eslint/no-unused-vars */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  Chat, ChatbotLimitPopup, PromptCategory, SetChat,
} from 'Types/ChatBotTypes';
import { ChatBotState } from 'Types/GlobalTypes';
import {
  AskGilroyData, ChatBotMaintainTab, ChatBotPromptsPayload, ChatFeedback,
  ChatHistoryRequest, ClearChatRequest, CompanyChatBot, DocumentChatBot,
  EditChatRequest, GroupChatBot,
} from 'Types/RequestBodies';
import { getLocalStorageValue } from 'Utils/UtilityFunctions';

export const initialState: ChatBotState = {
  generateEmailData: {} as AskGilroyData,
  emailLoading: false,
  generatedEmailView: false,
  promptsLoading: false,
  companyPrompts: [],
  personPrompts: [],
  groupPrompts: [],
  industryPrompts: [],
  documentPrompts: [],
  chat: [] as Chat[],
  timeout: false,
  newAPICall: false,
  chatLoading: false,
  chatEnded: false,
  chatLoaded: false,
  showPromptLibrary: false,
  limitPopup: {} as ChatbotLimitPopup,
  saveUrl: '',
  saveTab: '',
  saveProfileName: '',
  stopGeneratingIds: [],
  pendingChatId: 0,
  deleteChatId: 0,
  editChatLoading: false,
  editInProgressId: 0,
  chatId: 0,
  isDeletePopupVisible: false,
};

const ChatBotSlice = createSlice({
  name: 'chatBot',
  initialState,
  reducers: {
    generateEmail(state, action: PayloadAction<AskGilroyData>) {
      const { payload } = action;
      state.emailLoading = true;
      state.generateEmailData = payload;
      state.pendingChatId = state.chatId;
      state.chatId += 1;
    },
    setChat(state, action: PayloadAction<SetChat>) {
      const chatArray = action.payload.chat;
      const id = action.payload?.id as number;
      if (state.stopGeneratingIds.includes(id)) {
        state.deleteChatId = chatArray[0].id;
      } else {
        if (chatArray.length === 0) {
          state.chatEnded = true;
        }
        chatArray.forEach((chatObject) => {
          const chat = chatObject;
          chat.chat = chat.chat.replace(/\n/g, '<br />');
          const senderName = getLocalStorageValue('fullname');
          chat.chat = chat.chat.replace(/\[Your Name\]/gi, senderName || '');
          if (chatArray?.length === 1) {
            state.chat = [...state.chat, chat];
          } else {
            state.chat = [chat, ...state.chat];
          }
        });
        state.generatedEmailView = true;
        state.chatLoaded = true;
        state.emailLoading = false;
        state.chatLoading = false;
      }
    },
    companyChatBot(state, action: PayloadAction<CompanyChatBot>) {
      const { payload } = action;
      state.emailLoading = true;
      state.generateEmailData = payload;
      state.pendingChatId = state.chatId;
      state.chatId += 1;
    },
    groupChatBot(state, action: PayloadAction<GroupChatBot>) {
      const { payload } = action;
      state.emailLoading = true;
      state.generateEmailData = payload;
      state.pendingChatId = state.chatId;
      state.chatId += 1;
    },
    documentChatBot(state, action: PayloadAction<DocumentChatBot>) {
      const { payload } = action;
      state.emailLoading = true;
      state.generateEmailData = payload;
      state.pendingChatId = state.chatId;
      state.chatId += 1;
    },
    setChatLoading(state, action: PayloadAction<boolean>) {
      state.emailLoading = action.payload;
    },
    resetChat(state) {
      state.chat = [];
      state.emailLoading = false;
      state.generatedEmailView = false;
      state.showPromptLibrary = false;
    },
    clearChatHistory(state, action: PayloadAction<ClearChatRequest>) {
      state.showPromptLibrary = false;
    },
    setClearChatHistory(state, action: PayloadAction<string>) {
      const chatIds = action.payload.split(',').map((chatId) => chatId.trim());
      state.chat = state.chat.filter((chat) => chatIds.indexOf(chat.id.toString()) === -1);
    },
    setClearAllChatHistory(state) {
      state.chat = [];
      state.showPromptLibrary = true;
    },
    setGeneratedEmailView(state, action:PayloadAction<boolean>) {
      state.generatedEmailView = action.payload;
    },
    getChatBotPrompts(state, action:PayloadAction<ChatBotPromptsPayload>) {
      state.promptsLoading = true;
    },
    setChatBotPrompts(state, action:PayloadAction<PromptCategory[]>) {
      state.promptsLoading = false;
      switch (action.payload[0]?.type.toLowerCase()) {
        case 'company':
          state.companyPrompts = action.payload;
          break;
        case 'person':
          state.personPrompts = action.payload;
          break;
        case 'group':
          state.groupPrompts = action.payload;
          break;
        case 'industry':
          state.industryPrompts = action.payload;
          break;
        case 'document':
          state.documentPrompts = action.payload;
          break;
        default:
          break;
      }
    },
    sendFeedback(state, action: PayloadAction<ChatFeedback>) {

    },
    setFeedbackSubmitted(state, action: PayloadAction<string>) {
      const responseId = action.payload;
      state.chat = state.chat.map((chats) => {
        if (chats.responseId === responseId) {
          return { ...chats, feedbackSubmitted: true };
        }
        return chats;
      });
    },
    setTimeout(state, action: PayloadAction<boolean>) {
      state.timeout = action.payload;
    },
    setNewAPICall(state, action: PayloadAction<boolean>) {
      state.newAPICall = action.payload;
    },
    setChatbotPopup(state, action: PayloadAction<ChatbotLimitPopup>) {
      state.limitPopup = action.payload;
    },
    getChatHistory(state, action: PayloadAction<ChatHistoryRequest>) {
      state.chatLoading = true;
      state.chatEnded = false;
      state.chatLoaded = false;
    },
    saveUrl(state, action: PayloadAction<ChatBotMaintainTab>) {
      state.saveUrl = action.payload.url;
      state.saveTab = action.payload.tab;
    },
    saveProfileName(state, action: PayloadAction<string>) {
      state.saveProfileName = action.payload;
    },
    setStopGenerating(state, action: PayloadAction<number>) {
      state.stopGeneratingIds = [...state.stopGeneratingIds, action.payload];
      state.emailLoading = false;
    },
    editChatResponse(state, action: PayloadAction<EditChatRequest>) {
      state.editChatLoading = true;
    },
    updateChatResponse(state, action: PayloadAction<Chat>) {
      const chatIndex = state.chat.findIndex(
        (chat) => chat.responseId === action.payload.responseId,
      );
      if (chatIndex !== -1) {
        const updatedChats = [...state.chat];
        updatedChats[chatIndex] = action.payload;
        state.chat = updatedChats;
      }
      state.editChatLoading = false;
    },
    setEditChatLoading(state, action: PayloadAction<boolean>) {
      state.editChatLoading = action.payload;
    },
    setEditInProgressId(state, action: PayloadAction<number>) {
      state.editInProgressId = action.payload;
    },
    setIsDeletePopupVisible(state, action: PayloadAction<boolean>) {
      state.isDeletePopupVisible = action.payload;
    },
    resetChatbotPopup(state) {
      state.limitPopup.callToAction = '';
      state.limitPopup.contactUsEmail = '';
      state.limitPopup.popupText = '';
      state.limitPopup.showPlanPopup = 0;
    },
  },
});

export const { actions, reducer, name: sliceKey } = ChatBotSlice;
